import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { useStorage } from "@platform/hooks";

import { RFPS_DRAFT, RFPS_SUBMITTED } from "../constants";
import { RfpPageState } from "../constants/rfp";

const defaultColIdField = "details.dueDate";

type useRfpsTab = {
  setDebouncedQuery: (value: string) => void;
  setCurrStatus: (values: string[]) => void;
  setSortBy: (value: string) => void;
  setIsDescending: (value: boolean) => void;
  isBrokerPartner?: boolean;
  isDraftList?: boolean;
  rfpPageState: RfpPageState;
  resetPage?: () => void;
  sessionStorageKey: string;
};

type LocationState = { tabIndex?: number } | undefined;

export const useRfpsTab = ({
  setDebouncedQuery,
  setCurrStatus,
  setSortBy,
  setIsDescending,
  isBrokerPartner = false,
  isDraftList = false,
  rfpPageState = {},
  resetPage,
  sessionStorageKey,
}: useRfpsTab): {
  handleTabChange: (index: number) => void;
  tabIndex: number;
  initialTabIndex: number;
  setTabIndex: (value: number) => void;
} => {
  const { getStorageItem, setStorageItem } = useStorage("session");
  const location = useLocation();
  const locationState = location.state as LocationState;
  const initialTabIndex = useMemo(() => {
    if (isBrokerPartner) {
      return isDraftList ? RFPS_DRAFT : RFPS_SUBMITTED;
    }

    return locationState?.tabIndex ?? rfpPageState.tabIndex ?? RFPS_SUBMITTED;
  }, [locationState, rfpPageState, isBrokerPartner, isDraftList]);
  const [tabIndex, setTabIndex] = useState<number>(initialTabIndex);

  useEffect(() => {
    if (!rfpPageState.sortModel?.[0]?.colId) {
      setSortBy(
        initialTabIndex === RFPS_DRAFT ? "lastModifyDate" : defaultColIdField,
      );
    }

    if (!rfpPageState.sortModel?.sort) {
      setIsDescending(true);
    }
  }, [initialTabIndex, rfpPageState.sortModel, setIsDescending, setSortBy]);

  const handleTabChange = useCallback(
    (newTabIndex: number): void => {
      const currentStorage = getStorageItem(sessionStorageKey);
      setTabIndex(newTabIndex);
      setDebouncedQuery("");
      setCurrStatus([]);
      resetPage?.();
      // Set tab-specific default sorting
      if (newTabIndex === RFPS_DRAFT) {
        setSortBy("lastModifyDate");
        setIsDescending(true);
        currentStorage["sortModel"] = [
          {
            colId: "lastModifyDate",
            sort: "desc",
          },
        ];
      } else {
        setSortBy(defaultColIdField);
        setIsDescending(true);
        currentStorage["sortModel"] = [
          {
            colId: defaultColIdField,
            sort: "desc",
          },
        ];
      }

      setStorageItem(sessionStorageKey, currentStorage);
    },
    [
      getStorageItem,
      sessionStorageKey,
      setDebouncedQuery,
      setCurrStatus,
      resetPage,
      setStorageItem,
      setSortBy,
      setIsDescending,
    ],
  );

  return {
    handleTabChange,
    tabIndex,
    initialTabIndex,
    setTabIndex,
  };
};
